import * as R from "remeda";
import { MissionEventRecord, MissionRecord } from "../types";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import Button from "@mui/material/Button";

import {
	Identifier,
	RecordContextProvider,
	useListContext,
	useRecordContext,
} from "react-admin";
import { MissionSummaryExportButton } from "./MissionSummaryExportButton";
import { TimelineEntry } from "./TimelineEntry";
import { UpdateEventDialog } from "./UpdateEventDialog";
import {
	displayTime,
	eventMarked,
	isEta,
	useShowMissionEventActions,
} from "./functions";
import { missionActive } from "../functions";
import { useScreenWidth } from "../../../media";
import { useState } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

export const Timeline = () => {
	const mission = useRecordContext<MissionRecord>();
	const { data: missionEvents = [], isLoading } =
		useListContext<MissionEventRecord>();

	const [editedEventId, setEditedEventId] = useState<Identifier | null>(null);

	const showMissionEventActions = useShowMissionEventActions(mission);

	const timelineEvents = R.pipe(
		missionEvents,
		R.filter(eventMarked),
		R.sortBy(displayTime),
		R.reverse()
	);

	const closeEditDialog = () => {
		setEditedEventId(null);
	};

	const greaterThanSm = useScreenWidth("sm");

	return (
		<Box sx={{ maxWidth: 800 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="h2" gutterBottom>
					Event timeline
				</Typography>
				{!missionActive(mission) && <MissionSummaryExportButton />}
			</Box>
			{isLoading ? (
				<Box padding={2}>
					<LinearProgress />
				</Box>
			) : (
				<Table
					size="small"
					sx={{
						border: 0,
						borderColor: "divider",
						"& .MuiTableCell-root": {
							padding: { xs: "6px", mobile: "6px 16px" },
						},
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ border: 0 }}></TableCell>
							<TableCell>Time</TableCell>
							<TableCell>Event</TableCell>
							{greaterThanSm && showMissionEventActions && (
								<TableCell>Actions</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{!missionActive(mission) && (
							<TimelineEntry
								name="Mission ended"
								time={new Date(mission.end_time)}
							/>
						)}
						{timelineEvents.map(
							(missionEvent: MissionEventRecord, idx: number) => {
								const time = new Date(displayTime(missionEvent)!);

								return (
									<RecordContextProvider
										key={missionEvent.id}
										value={missionEvent}
									>
										{editedEventId === missionEvent.id && (
											<UpdateEventDialog closeDialog={closeEditDialog} />
										)}
										<TimelineEntry
											name={missionEvent.name}
											isEta={isEta(missionEvent)}
											latest={idx === 0 && missionActive(mission)}
											time={time}
											comments={missionEvent.comments}
											actions={
												showMissionEventActions ? (
													<Button
														variant={greaterThanSm ? "contained" : "text"}
														color={greaterThanSm ? "neutral" : "primary"}
														size="small"
														onClick={() => setEditedEventId(missionEvent.id)}
														sx={{
															padding: {
																xs: 0,
																mobile: "4px 5px",
															},
														}}
													>
														Edit
													</Button>
												) : undefined
											}
										/>
									</RecordContextProvider>
								);
							}
						)}

						<TimelineEntry
							name="Mission started"
							time={new Date(mission.start_time)}
							actions={showMissionEventActions && <></>}
						/>
					</TableBody>
				</Table>
			)}
		</Box>
	);
};
